/**
 * DL-01 — "resume / never download twice" (Christian 2026-10-21). PURE, the
 * hasher injected.
 *
 * Before writing, for every manifest entry the file of that name on disk is:
 *   (1) compared by SIZE with `bytes` — differs or absent → download;
 *   (2) equal → its MD5 computed and compared with the manifest's `md5`
 *       (= `images.content_md5`) — equal → SKIP ("already present");
 *       differs → re-download and overwrite (a partial file from an aborted
 *       run, or a changed file).
 * An entry WITHOUT an md5 (older rows) falls back to size-only and is reported
 * as "verified by size". A `manifest.json` already in the folder is only a
 * hint — the file bytes are the truth, so it is never consulted here.
 */
import type { ManifestImage } from "./manifestApi.js";

export type VerifyOutcome =
  /** size and md5 match — skipped, counted "already present" */
  | "present_md5"
  /** size matches, the row has no md5 — skipped, "verified by size" */
  | "present_size"
  /** absent, or size / md5 differs — downloaded (overwritten) */
  | "download";

export type VerifyReason = "missing" | "size_differs" | "md5_differs";

export interface VerifyVerdict {
  outcome: VerifyOutcome;
  reason?: VerifyReason | undefined;
}

/** What the rule needs of the file on disk (null when there is none). */
export interface ExistingFile {
  size: number;
  md5: () => Promise<string>;
}

export async function verifyEntry(
  entry: Pick<ManifestImage, "bytes" | "md5">,
  existing: ExistingFile | null,
): Promise<VerifyVerdict> {
  if (existing === null) return { outcome: "download", reason: "missing" };
  // A row without a recorded size or md5 (legacy, `blob_state = unknown`:
  // lyse/v1 8.7.1 lists it with `bytes` 0, `md5` null) cannot be verified by
  // size — a non-empty file on disk counts as present ("verified by size" is
  // the closest truthful outcome); an empty one is downloaded again.
  if (entry.bytes === 0 && entry.md5 === null)
    return existing.size > 0
      ? { outcome: "present_size" }
      : { outcome: "download", reason: "size_differs" };
  if (existing.size !== entry.bytes)
    return { outcome: "download", reason: "size_differs" };
  if (entry.md5 === null) return { outcome: "present_size" };
  const onDisk = (await existing.md5()).toLowerCase();
  return onDisk === entry.md5
    ? { outcome: "present_md5" }
    : { outcome: "download", reason: "md5_differs" };
}

export interface VerifySummary {
  /** skipped with the md5 proved */
  presentMd5: number;
  /** skipped by size only (no md5 on the row) */
  presentSize: number;
  toDownload: number;
  /** the bytes still to fetch — the "required" number before the run */
  requiredBytes: number;
}

export function summarizeVerification(
  entries: readonly Pick<ManifestImage, "bytes">[],
  verdicts: readonly VerifyVerdict[],
): VerifySummary {
  const out: VerifySummary = {
    presentMd5: 0,
    presentSize: 0,
    toDownload: 0,
    requiredBytes: 0,
  };
  verdicts.forEach((v, k) => {
    if (v.outcome === "present_md5") out.presentMd5++;
    else if (v.outcome === "present_size") out.presentSize++;
    else {
      out.toDownload++;
      out.requiredBytes += entries[k]?.bytes ?? 0;
    }
  });
  return out;
}
